import { useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";
import { Link } from "../components/Link";
import { SchoolPicker } from "../components/SchoolPicker";
import { useNavigate } from "../router";

import type { ApiErrorCode } from "../api/client";
import type { School } from "@canvaschool/contracts";

/** A teacher picks the school once; the account page links here to change it later. */
export const SchoolSetupPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const current =
    auth.state.status === "authenticated"
      ? auth.state.session.teacherProfile?.school ?? null
      : null;
  const [school, setSchool] = useState<School | null>(current);
  const [error, setError] = useState<ApiErrorCode | null>(null);
  const [pending, setPending] = useState(false);

  return (
    <section className="canvaschool-auth">
      <h1>{current ? "학교 바꾸기" : "학교를 선택해 주세요"}</h1>
      {!current && (
        <p>
          갤러리 등에서 학교별로 묶기 위해 학교를 한 번 선택합니다. 나중에
          오른쪽 위 내 아이디(마이페이지)에서 바꿀 수 있습니다.
        </p>
      )}
      <form
        aria-label="학교 선택"
        className="canvaschool-form"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!school || pending) {
            return;
          }
          setPending(true);
          setError(null);
          try {
            const saved = await auth.api.setMySchool(school.code);
            auth.updateTeacherProfile(saved);
            navigate("/classes", { replace: true, force: true });
          } catch (failure) {
            setError(auth.handleError(failure));
            setPending(false);
          }
        }}
      >
        <div className="canvaschool-field">
          <label htmlFor="canvaschool-school-setup">학교</label>
          <SchoolPicker
            id="canvaschool-school-setup"
            value={school}
            onChange={setSchool}
            disabled={pending}
            autoFocus
          />
        </div>
        {error && <ErrorAlert code={error} boxed />}
        <button
          type="submit"
          className="canvaschool-btn-primary"
          disabled={pending || !school}
        >
          저장
        </button>
        {current && <Link to="/classes">취소</Link>}
      </form>
    </section>
  );
};
